import { useParams } from 'react-router-dom';
import { useAuth } from '../../shared/context';
import { useDataRoomBorrowerParticipantsQuery, useDealQuery } from '../../shared/hooks';
import { CerebroBreadcrumb, CerebroPageContainer, CerebroSecondaryButton, CerebroText } from '../../shared/components';
import { useState } from 'react';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, Grid, Skeleton, Tab } from '@mui/material';
import { AvailableBorrowerList } from './AvailableBorrowerList';
import { BorrowerList } from './BorrowerList';
import { CompanyContact, CompanyContactDataRoom } from '../../shared/models';
import { AddColleagueDrawer } from './AddColleagueDrawer';
import { EditColleagueAccessDrawer } from './EditColleagueAccessDrawer';
import { AddColleagueAccessDrawer } from './AddColleagueAccessDrawer';

export const Colleagues: React.FC = () => {
	const { currentUser } = useAuth();
	const { guid } = useParams<'guid'>();
	const dealQuery = useDealQuery(guid);
	const dataRoomGuid = dealQuery.data?.dataRoomGuid ?? '';
	const companyGuid = dealQuery.data?.companyGuid ?? '';
	const [tab, setTab] = useState('full');
	const handleTabChange = (event: any, newValue: string) => {
		setTab(newValue);
	};
	const [editAccess, setEditAccess] = useState<CompanyContactDataRoom | null>(null);
	const [addAccess, setAddAccess] = useState<CompanyContact | null>(null);
	const [addColleague, setAddColleague] = useState(false);

	const contactsQuery = useDataRoomBorrowerParticipantsQuery(dataRoomGuid, {
		enabled: dealQuery.isSuccess,
		retry: false,
	});

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Colleagues</CerebroBreadcrumb>
			<CerebroText sx={{ marginBottom: 3 }}>
				Use this page to give your colleagues access to your loan application.
			</CerebroText>

			{(contactsQuery.isLoading || dealQuery.isLoading) && (
				<>
					<Skeleton height={200} />
					<Skeleton height={200} />
					<Skeleton height={200} />
				</>
			)}

			{contactsQuery.isSuccess && dealQuery.isSuccess && (
				<>
					{contactsQuery.data.hasFullAccess && (
						<Grid container>
							<Grid item xs={12}>
								<CerebroSecondaryButton sx={{ marginRight: 1 }} onClick={() => setAddColleague(true)}>
									Add Contact
								</CerebroSecondaryButton>
							</Grid>
						</Grid>
					)}

					<TabContext value={tab}>
						<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
							<TabList onChange={handleTabChange}>
								<Tab label="Full Access" value="full" />
								<Tab label="Limited Access" value="limited" />
								<Tab label="3rd Party" value="3rd" />
								<Tab label="None" value="none" />
							</TabList>
						</Box>

						<TabPanel value="full">
							<BorrowerList
								currentUser={currentUser}
								hasFullAccess={contactsQuery.data.hasFullAccess}
								borrowers={contactsQuery.data.dataRoomContacts.filter(
									w => w.accessType == 'FullAccess'
								)}
								onEdit={setEditAccess}
							/>
						</TabPanel>
						<TabPanel value="limited">
							<BorrowerList
								currentUser={currentUser}
								hasFullAccess={contactsQuery.data.hasFullAccess}
								borrowers={contactsQuery.data.dataRoomContacts.filter(
									w => w.accessType == 'LimitedAccess'
								)}
								onEdit={setEditAccess}
							/>
						</TabPanel>
						<TabPanel value="3rd">
							<BorrowerList
								currentUser={currentUser}
								hasFullAccess={contactsQuery.data.hasFullAccess}
								borrowers={contactsQuery.data.dataRoomContacts.filter(
									w => w.isThirdPartyContact == true
								)}
								onEdit={setEditAccess}
							/>
						</TabPanel>
						<TabPanel value="none">
							<AvailableBorrowerList
								currentUser={currentUser}
								hasFullAccess={contactsQuery.data.hasFullAccess}
								borrowers={contactsQuery.data.availableContacts}
								onAdd={setAddAccess}
							/>
						</TabPanel>
					</TabContext>

					<AddColleagueDrawer
						open={addColleague}
						isContactParentCompany={contactsQuery.data.isContactParentCompany || currentUser.isEmployeeUser}
						companies={contactsQuery.data.companies}
						companyGuid={companyGuid}
						dataRoomGuid={dataRoomGuid}
						currentUser={currentUser}
						onClose={() => setAddColleague(false)}
					/>

					<AddColleagueAccessDrawer
						record={addAccess}
						companyGuid={companyGuid}
						dataRoomGuid={dataRoomGuid}
						currentUser={currentUser}
						onClose={() => setAddAccess(null)}
					/>

					<EditColleagueAccessDrawer
						record={editAccess}
						companyGuid={companyGuid}
						dataRoomGuid={dataRoomGuid}
						currentUser={currentUser}
						onClose={() => setEditAccess(null)}
					/>
				</>
			)}
		</CerebroPageContainer>
	);
};

export function getAccessStatus(record: CompanyContactDataRoom) {
	if (record.accessType === 'FullAccess') {
		return 'Full Access';
	} else if (record.accessType === 'LimitedAccess') {
		return 'Limited Access';
	}

	return 'No Access';
}
